import { useCallback, useEffect, useMemo, useState } from 'react';
import { FLASHCARDS, FLASHCARD_TOPICS } from './data/gmatQuantData';
import { createProgress, gradeCard, isDue, pickNextCard } from './srsEngine';
import { loadProgress, resetProgress, saveProgress } from './progressStorage';
import type { CardProgress, Flashcard, ProgressMap, SessionStats } from './types';

const RECENT_QUEUE_SIZE = 8;

const emptyStats: SessionStats = {
  seen: 0,
  correct: 0,
  wrong: 0,
  bestStreak: 0,
  currentStreak: 0,
  masteredThisSession: [],
};

export function useFlashcardStudy(userId: string | undefined) {
  const storageId = userId || 'anonymous';

  const [selectedTopicIds, setSelectedTopicIds] = useState<Set<string>>(
    () => new Set(FLASHCARD_TOPICS.map(t => t.id))
  );
  const [progressMap, setProgressMap] = useState<ProgressMap>({});
  const [sessionActive, setSessionActive] = useState(false);
  const [currentCard, setCurrentCard] = useState<Flashcard | null>(null);
  const [isFlipped, setIsFlipped] = useState(false);
  const [recentCardIds, setRecentCardIds] = useState<string[]>([]);
  const [stats, setStats] = useState<SessionStats>(emptyStats);
  const [lastFeedback, setLastFeedback] = useState<'correct' | 'wrong' | null>(null);

  useEffect(() => {
    setProgressMap(loadProgress(storageId));
  }, [storageId]);

  const persist = useCallback((next: ProgressMap) => {
    setProgressMap(next);
    saveProgress(storageId, next);
  }, [storageId]);

  const cardsByTopic = useMemo(() => {
    const map = new Map<string, Flashcard[]>();
    for (const topic of FLASHCARD_TOPICS) map.set(topic.id, []);
    for (const card of FLASHCARDS) map.get(card.topicId)?.push(card);
    return map;
  }, []);

  const topicStats = useMemo(() => {
    const now = Date.now();
    return FLASHCARD_TOPICS.map(topic => {
      const cards = cardsByTopic.get(topic.id) ?? [];
      let due = 0;
      let mastered = 0;
      for (const card of cards) {
        const progress = progressMap[card.id];
        if (progress?.state === 'mastered') mastered += 1;
        if (isDue(progress, now)) due += 1;
      }
      return { topic, total: cards.length, due, mastered };
    });
  }, [cardsByTopic, progressMap]);

  const selectedCards = useMemo(
    () => FLASHCARDS.filter(c => selectedTopicIds.has(c.topicId)),
    [selectedTopicIds]
  );

  const overallStats = useMemo(() => {
    const total = FLASHCARDS.length;
    let mastered = 0;
    let due = 0;
    const now = Date.now();
    for (const card of FLASHCARDS) {
      const progress = progressMap[card.id];
      if (progress?.state === 'mastered') mastered += 1;
      if (isDue(progress, now)) due += 1;
    }
    return { total, mastered, due };
  }, [progressMap]);

  const toggleTopic = useCallback((topicId: string) => {
    setSelectedTopicIds(prev => {
      const next = new Set(prev);
      if (next.has(topicId)) next.delete(topicId);
      else next.add(topicId);
      return next;
    });
  }, []);

  const selectAllTopics = useCallback(() => {
    setSelectedTopicIds(new Set(FLASHCARD_TOPICS.map(t => t.id)));
  }, []);

  const clearTopics = useCallback(() => {
    setSelectedTopicIds(new Set());
  }, []);

  const drawNextCard = useCallback((pool: Flashcard[], progress: ProgressMap, recent: string[]) => {
    return pickNextCard({ cards: pool, progressMap: progress, recentCardIds: recent });
  }, []);

  const startSession = useCallback(() => {
    if (selectedCards.length === 0) return;
    setStats(emptyStats);
    setRecentCardIds([]);
    setLastFeedback(null);
    const next = drawNextCard(selectedCards, progressMap, []);
    setCurrentCard(next);
    setIsFlipped(false);
    setSessionActive(true);
  }, [selectedCards, progressMap, drawNextCard]);

  const endSession = useCallback(() => {
    setSessionActive(false);
    setCurrentCard(null);
    setIsFlipped(false);
    setLastFeedback(null);
  }, []);

  const flipCard = useCallback(() => setIsFlipped(prev => !prev), []);

  const grade = useCallback((correct: boolean) => {
    if (!currentCard) return;

    const prior = progressMap[currentCard.id] ?? createProgress(currentCard.id);
    const nextProgress = gradeCard(prior, correct);
    const nextMap = { ...progressMap, [currentCard.id]: nextProgress };

    const justMastered =
      nextProgress.state === 'mastered' && prior.state !== 'mastered';

    setStats(prev => {
      const currentStreak = correct ? prev.currentStreak + 1 : 0;
      return {
        seen: prev.seen + 1,
        correct: prev.correct + (correct ? 1 : 0),
        wrong: prev.wrong + (correct ? 0 : 1),
        bestStreak: Math.max(prev.bestStreak, currentStreak),
        currentStreak,
        masteredThisSession: justMastered
          ? [...prev.masteredThisSession, currentCard.id]
          : prev.masteredThisSession,
      };
    });

    setLastFeedback(correct ? 'correct' : 'wrong');
    persist(nextMap);

    const nextRecent = [currentCard.id, ...recentCardIds].slice(0, RECENT_QUEUE_SIZE);
    setRecentCardIds(nextRecent);

    const next = drawNextCard(selectedCards, nextMap, nextRecent);
    setCurrentCard(next);
    setIsFlipped(false);
  }, [currentCard, progressMap, persist, recentCardIds, selectedCards, drawNextCard]);

  const resetAllProgress = useCallback(() => {
    resetProgress(storageId);
    setProgressMap({});
  }, [storageId]);

  const getCardProgress = useCallback(
    (cardId: string): CardProgress | undefined => progressMap[cardId],
    [progressMap]
  );

  return {
    topics: FLASHCARD_TOPICS,
    topicStats,
    selectedTopicIds,
    selectedCardCount: selectedCards.length,
    overallStats,
    toggleTopic,
    selectAllTopics,
    clearTopics,
    sessionActive,
    currentCard,
    isFlipped,
    flipCard,
    grade,
    startSession,
    endSession,
    stats,
    lastFeedback,
    resetAllProgress,
    getCardProgress,
  };
}
